import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SERVICE_LOOKUP } from 'src/environments/global.config';

@Injectable({
	providedIn: 'root',
})
export class IamPolicyService {
	protected readonly api: string = environment.api.iam;
	private principalServicesCache = new Map<string, { data: string[]; timestamp: number }>();
	private principalServicePermissionsCache = new Map<string, { data: any; timestamp: number }>();
	private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

	constructor(private readonly http: HttpClient) {
		const value = SERVICE_LOOKUP('iam');
		if (value != null && value.endpoint !== this.api) {
			this.api = value.endpoint;
		}
	}

	private principalPolicyChange = new BehaviorSubject('*');
	principalPolicyUpdate = this.principalPolicyChange.asObservable();

	updatedPrincipalPoliciy(principal: string) {
		this.principalPolicyChange.next(principal);

		// Clear service and service permissions cache for this principal
		this.principalServicesCache.delete(principal);
		for (const key of this.principalServicePermissionsCache.keys()) {
			if (key.startsWith(`${principal}:`)) {
				this.principalServicePermissionsCache.delete(key);
			}
		}
	}
	/**
	 * Get all services that the principal has permissions for
	 * @param user The user CRN
	 * @returns A list of service names
	 */
	public async getPrincipalServices(user: string): Promise<string[]> {
		return this.http.get<string[]>(this.api + '/v1/PolicyDocuments/' + user).toPromise();
	}

	public async getActualPrincipalServices(user: string): Promise<string[]> {
		const cached = this.principalServicesCache.get(user);
		const now = Date.now();

		if (cached && now - cached.timestamp < this.CACHE_TTL) {
			return cached.data;
		}

		const data = await this.http.get<string[]>(this.api + '/v1/PolicyDocuments/' + user + '/Combined').toPromise();
		this.principalServicesCache.set(user, { data, timestamp: now });
		return data;
	}

	/**
	 * Delete specified services from the principal
	 * @param user The user CRN
	 * @param services The services to delete
	 * @returns
	 */
	public async deletePrincipalServices(user: string, services: string[]): Promise<any> {
		const response = await this.http
			.delete<any>(this.api + '/v1/PolicyDocuments/' + user, { body: { services: services } })
			.toPromise();
		this.updatedPrincipalPoliciy(user);
		return response;
	}

	/**
	 * Get permissions that the principal has for the given service
	 * @param user The user
	 * @param service
	 * @returns
	 */
	public async getPrincipalServicePermissions(user: string, service: string): Promise<Map<string, any>> {
		return this.http.get<Map<string, any>>(this.api + '/v1/PolicyDocuments/' + user + '/' + service).toPromise();
	}

	public async getActualPrincipalServicePermissions(user: string, service: string): Promise<any> {
		const cacheKey = `${user}:${service}`;
		const cached = this.principalServicePermissionsCache.get(cacheKey);
		const now = Date.now();

		if (cached && now - cached.timestamp < this.CACHE_TTL) {
			return cached.data;
		}

		const data = await this.http.get<any>(this.api + '/v1/PolicyDocuments/' + user + '/' + service + '/Combined').toPromise();
		this.principalServicePermissionsCache.set(cacheKey, { data, timestamp: now });
		return data;
	}

	public async updatePrincipalServicePermissions(
		user: string,
		service: string,
		document: { [permission: string]: string[] }
	): Promise<void> {
		const response = await this.http.put<any>(this.api + '/v1/PolicyDocuments/' + user, { service, policy: document }).toPromise();
		this.updatedPrincipalPoliciy(user);
		return response;
	}

	public async addUserToTrustPolicy(role: string, user: string): Promise<any> {
		return this.http.put<any>(this.api + '/v1/TrustPolicy/' + role, { addPrincipal: user }).toPromise();
	}

	public async removeUserFromTrustPolicy(role: string, user: string): Promise<any> {
		return this.http.put<any>(this.api + '/v1/TrustPolicy/' + role, { remPrincipal: user }).toPromise();
	}

	public async overwriteTrustPolicy(role: string, users: string[]): Promise<any> {
		return this.http.put<any>(this.api + '/v1/TrustPolicy/' + role, { trustPolicy: users }).toPromise();
	}
}
